import React, { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { Robot, PaperPlaneRight } from "@phosphor-icons/react";
import { Spinner } from "../../components/shared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import api, { apiErr } from "../../lib/api";

const PROMPTS = [
  "My sales are low. What should I do?",
  "Suggest ticket pricing",
  "Which ticket category performs best?",
  "Write a WhatsApp campaign for my event",
  "Suggest my next action",
];

export default function Copilot() {
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState("all");
  const [messages, setMessages] = useState([{ role: "ai", text: "I'm your Organizer Copilot. I use your real event data — ask me about sales, pricing, marketing or your next best action." }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { api.get("/organizer/events").then((r) => setEvents(r.data.events)); }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || loading) return;
    setMessages((m) => [...m, { role: "user", text: q }]);
    setInput(""); setLoading(true);
    try {
      const { data } = await api.post("/ai/copilot", { message: q, event_id: eventId === "all" ? null : eventId });
      setMessages((m) => [...m, { role: "ai", text: data.reply }]);
    } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  };

  return (
    <div className="max-w-2xl mx-auto flex flex-col min-h-[calc(100vh-8rem)]">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="grid place-items-center w-10 h-10 rounded-full bg-primary/10 text-primary"><Robot weight="fill" size={20} /></span>
          <h1 className="font-heading text-2xl font-semibold tracking-tight">AI Copilot</h1>
        </div>
        <Select value={eventId} onValueChange={setEventId}>
          <SelectTrigger className="w-44" data-testid="copilot-event"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All events</SelectItem>{events.map((e) => <SelectItem key={e.id} value={e.id}>{e.title}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="flex-1 space-y-4">
        {messages.map((m, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className={m.role === "user" ? "flex justify-end" : ""}>
            <div className={m.role === "user" ? "max-w-[80%] rounded-2xl rounded-br-sm bg-foreground text-background px-4 py-2.5" : "max-w-[85%] rounded-2xl rounded-bl-sm glass border border-border px-4 py-3 whitespace-pre-wrap"}>{m.text}</div>
          </motion.div>
        ))}
        {loading && <div className="flex items-center gap-2 text-muted-foreground text-sm"><Spinner className="w-4 h-4 text-primary" /> Analysing your data…</div>}
        <div ref={endRef} />
      </div>

      {messages.length <= 1 && (
        <div className="flex flex-wrap gap-2 my-4">
          {PROMPTS.map((p) => <button key={p} onClick={() => send(p)} data-testid="copilot-prompt" className="px-3 py-2 rounded-full border border-border bg-card text-sm hover:border-primary/40 btn-press">{p}</button>)}
        </div>
      )}

      <div className="sticky bottom-4 mt-4">
        <div className="flex items-center gap-2 glass border border-border rounded-full p-1.5 pl-4">
          <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Ask about your sales, pricing, marketing…" className="border-0 bg-transparent focus-visible:ring-0 px-0" data-testid="copilot-input" />
          <Button onClick={() => send()} disabled={loading} className="w-10 h-10 p-0 rounded-full" data-testid="copilot-send"><PaperPlaneRight size={18} /></Button>
        </div>
      </div>
    </div>
  );
}
